import { canGoFullscreen, coarsePointer, isFullscreen, toggleFullscreen } from "@massivewebgames/screen";
import type { Action } from "./bindings";

/** What the player is doing, which decides the buttons shown. */
export type TouchMode = "foot" | "helm" | "turret";
/** What a finger on the world does on foot: hold to dig (and swing), or tap to place (and use the helm, turrets, hatches, chests, furnaces). */
export type TouchTool = "dig" | "place";
export type TouchPreference = "auto" | "on" | "off";

/** Where the player is, from the world (see touchSituation): which of the buttons that aren't always useful to show. */
export interface TouchSituation {
  /** The harpoon we're manning has its line out: leaving lets that go first. */
  lineOut: boolean;
  /** Something in hand: Throw puts it down (see DROPS), and off the rock it pushes you too (see THROW). */
  canThrow: boolean;
  /** Out in open space with no way to push: all that's left is to start over. */
  adrift: boolean;
  /** ↺ ↻ would turn your body (see canSpin). */
  canTurn: boolean;
}

/** Buttons held down rather than pressed. */
type Hold = "brace" | "fire" | "turnLeft" | "turnRight";

interface ButtonDef {
  id: string;
  label: string;
  /** Held: on while the finger is down. */
  hold?: Hold;
  /** Pressed: an action, as a key press of it. */
  press?: Action;
  /** Pressed: leave the helm or the turret (a right click, on a mouse). */
  leave?: true;
  size?: "big" | "wide";
  /** Shown only then (otherwise always). */
  when?: "placing" | "adrift" | "canThrow" | "canTurn";
}

const FOOT: ButtonDef[] = [
  { id: "brace", label: "Leap", hold: "brace", size: "big" },
  { id: "turnR", label: "↻", hold: "turnRight", when: "canTurn" },
  { id: "turnL", label: "↺", hold: "turnLeft", when: "canTurn" },
  { id: "tool", label: "" },
  { id: "throw", label: "Throw", press: "throw", when: "canThrow" },
  { id: "rotate", label: "Rotate", press: "rotate", when: "placing" },
  { id: "startOver", label: "Start over", press: "respawn", when: "adrift", size: "wide" },
];
const HELM: ButtonDef[] = [
  { id: "turnR", label: "↻", hold: "turnRight", size: "big" },
  { id: "turnL", label: "↺", hold: "turnLeft", size: "big" },
  { id: "fire", label: "Fire", hold: "fire" },
  { id: "leave", label: "Leave helm", leave: true, size: "wide" },
];
const TURRET: ButtonDef[] = [
  { id: "fire", label: "Fire", hold: "fire", size: "big" },
  { id: "out", label: "Climb out", leave: true, size: "wide" },
];
const BUTTONS: Record<TouchMode, ButtonDef[]> = { foot: FOOT, helm: HELM, turret: TURRET };

/** How far (px) the knob travels; the stick follows a thumb that goes further, so turning back is never more than a throw away. */
const STICK_RADIUS = 48;
/** Shares of the throw: below the first the stick counts as centred, by the second it's full tilt (a thumb's small move should crawl, not creep). */
const STICK_DEAD = 0.15;
const STICK_FULL = 0.7;

/** A tilt (share of the throw, signed) as the push it asks for, -1..1. */
export function stickCurve(v: number): number {
  const a = Math.abs(v);
  if (a < STICK_DEAD) return 0;
  return Math.sign(v) * Math.min(1, (a - STICK_DEAD) / (STICK_FULL - STICK_DEAD));
}

/**
 * Touch controls, over the game on phones and tablets: a stick where the
 * left thumb lands — plain and analog, since there's nothing it can set off by
 * accident any more (the push off is Leap's own button) — buttons for the
 * right thumb that change with what you are doing (on foot, at the helm, in a
 * turret), a finger on the world to aim, dig or place, and two to zoom. Input
 * reads it each step the way it reads the gamepad.
 */
export class TouchControls {
  /** An action button was pressed (one-shot, like a key). */
  onPress: (action: Action) => void = () => {};
  /** Leave helm or Climb out was pressed. */
  onLeave: () => void = () => {};
  /** A finger on the world moved: the pointer, in client px. */
  onPointer: (x: number, y: number) => void = () => {};
  /** Two fingers moved apart or together: the camera distance, times this. */
  onZoom: (factor: number) => void = () => {};
  onChat: () => void = () => {};
  onHelp: () => void = () => {};
  /** Open the big map (the small one is hidden on short screens). */
  onMap: () => void = () => {};
  /** Shown or hidden (the setting, or a touch screen turning out to be in use). */
  onEnabled: (on: boolean) => void = () => {};

  enabled = false;
  mode: TouchMode = "foot";
  tool: TouchTool = "dig";
  /** The stick's tilt, each -1..1 (y up). */
  readonly move = { x: 0, y: 0 };
  /** A finger is on the world (not on the stick or a button). */
  worldDown = false;

  private preference: TouchPreference = "auto";
  private readonly held = new Set<Hold>();
  private readonly root = document.createElement("div");
  private readonly stickZone = document.createElement("div");
  private readonly stick = document.createElement("div");
  private readonly knob = document.createElement("div");
  private readonly cluster = document.createElement("div");
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private stickPointer: number | null = null;
  private stickAt = { x: 0, y: 0 };
  private situation: TouchSituation = { lineOut: false, canThrow: false, adrift: false, canTurn: false };
  /** Fingers on the world, by pointer id; two of them pinch. */
  private readonly fingers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;

  constructor(hud: HTMLElement) {
    this.root.id = "touch";
    this.stickZone.className = "touch-stick-zone";
    this.stick.className = "touch-stick";
    this.knob.className = "touch-knob";
    this.stick.append(this.knob);
    this.stickZone.append(this.stick);
    this.cluster.className = "touch-buttons";
    const top = document.createElement("div");
    top.className = "touch-top";
    top.append(
      this.smallButton("Bag", "Inventory and crafting", () => this.onPress("inventory")),
      this.smallButton("Map", "The map", () => this.onMap()),
      this.smallButton("Chat", "Chat", () => this.onChat()),
      this.smallButton("Eat", "Eat (heal)", () => this.onPress("eat")),
      this.smallButton("?", "Controls", () => this.onHelp()),
    );
    // Full screen is what finally puts a phone's URL bar away (and the game asks for it on launch too: see main.ts).
    if (canGoFullscreen()) {
      const full = this.smallButton("⛶", "Full screen", () => void toggleFullscreen({ landscape: true }));
      const mark = () => {
        const on = isFullscreen();
        full.classList.toggle("on", on);
        full.title = on ? "Leave full screen" : "Full screen";
      };
      document.addEventListener("fullscreenchange", mark);
      mark();
      top.append(full);
    }
    this.root.append(this.stickZone, this.cluster, top);
    hud.append(this.root);
    // The row sits just under the top-left panel, which grows and shrinks (a steampack's bar, a ship's fuel).
    const panel = hud.querySelector<HTMLElement>(".top-left");
    if (panel) new ResizeObserver(() => (top.style.top = `${panel.offsetTop + panel.offsetHeight + 6}px`)).observe(panel);
    // A long press isn't a right click here (the stick is often held still).
    this.root.addEventListener("contextmenu", (e) => e.preventDefault());
    this.wireStick();
    this.buildButtons();
  }

  /** The setting: on, off, or on when the screen is a touch screen (and the moment a finger touches the game). */
  setPreference(p: TouchPreference): void {
    this.preference = p;
    this.setEnabled(p === "on" || (p === "auto" && coarsePointer()));
  }

  private setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    this.root.classList.toggle("on", on);
    if (!on) this.releaseAll();
    this.onEnabled(on);
  }

  /** What the player is doing now: the buttons follow. */
  setContext(mode: TouchMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    // Only the buttons: the thumb on the stick carries on.
    this.releaseButtons();
    this.buildButtons();
  }

  /** Where the player is now: which buttons show. */
  setSituation(s: TouchSituation): void {
    const was = this.situation;
    this.situation = s;
    if (s.lineOut !== was.lineOut || s.adrift !== was.adrift || s.canThrow !== was.canThrow || s.canTurn !== was.canTurn) this.showButtons();
  }

  /** The stick, as it is: it crawls you along the rock, flies you, swims, climbs — whatever you're doing, any way you like. */
  get moveX(): number {
    return this.move.x;
  }

  get moveY(): number {
    return this.move.y;
  }

  get stickActive(): boolean {
    return this.stickPointer !== null && Math.hypot(this.move.x, this.move.y) > 0;
  }

  isHeld(h: Hold): boolean {
    return this.held.has(h);
  }

  /** The left mouse button's work: the fire button, or a finger on the world while digging on foot. */
  get leftDown(): boolean {
    return this.held.has("fire") || (this.mode === "foot" && this.tool === "dig" && this.worldDown);
  }

  /** The right mouse button's work: a finger on the world while placing on foot. */
  get rightDown(): boolean {
    return this.mode === "foot" && this.tool === "place" && this.worldDown;
  }

  // ---- fingers on the world (the canvas), passed on by Input

  fingerDown(e: PointerEvent): void {
    if (!this.enabled) {
      if (this.preference !== "auto") return;
      this.setEnabled(true);
    }
    this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.fingers.size === 1) {
      this.worldDown = true;
      this.onPointer(e.clientX, e.clientY);
    } else {
      // A second finger: zooming, not digging.
      this.worldDown = false;
      this.pinchDist = this.spread();
    }
  }

  fingerMove(e: PointerEvent): void {
    const f = this.fingers.get(e.pointerId);
    if (!f) return;
    f.x = e.clientX;
    f.y = e.clientY;
    if (this.fingers.size >= 2) {
      const d = this.spread();
      if (d > 0 && this.pinchDist > 0) this.onZoom(this.pinchDist / d);
      this.pinchDist = d;
    } else this.onPointer(e.clientX, e.clientY);
  }

  fingerUp(e: PointerEvent): void {
    if (!this.fingers.delete(e.pointerId)) return;
    // After a pinch the finger left over doesn't start digging.
    this.worldDown = false;
  }

  private spread(): number {
    const [a, b] = [...this.fingers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  // ---- the stick

  private wireStick(): void {
    const zone = this.stickZone;
    zone.addEventListener("pointerdown", (e) => {
      // A thumb we never heard lift (the browser took it) doesn't keep the stick from a new one.
      if (this.stickPointer !== null && zone.hasPointerCapture(this.stickPointer)) return;
      this.stickPointer = e.pointerId;
      zone.setPointerCapture(e.pointerId);
      this.stickAt = { x: e.clientX, y: e.clientY };
      this.placeStick();
      this.stick.classList.add("on");
      this.tilt(0, 0);
    });
    zone.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.stickPointer) return;
      let dx = e.clientX - this.stickAt.x;
      let dy = e.clientY - this.stickAt.y;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) {
        // Past the end of its throw the stick comes along behind the thumb.
        const k = STICK_RADIUS / len;
        this.stickAt.x += dx * (1 - k);
        this.stickAt.y += dy * (1 - k);
        this.placeStick();
        dx *= k;
        dy *= k;
      }
      this.tilt(dx, dy);
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== this.stickPointer) return;
      this.stickPointer = null;
      this.stick.classList.remove("on");
      this.tilt(0, 0);
    };
    zone.addEventListener("pointerup", end);
    zone.addEventListener("pointercancel", end);
    zone.addEventListener("lostpointercapture", end);
  }

  private placeStick(): void {
    const r = this.stickZone.getBoundingClientRect();
    this.stick.style.left = `${this.stickAt.x - r.left}px`;
    this.stick.style.top = `${this.stickAt.y - r.top}px`;
  }

  /** The knob `dx`,`dy` px from the stick's middle (y down, as on screen). */
  private tilt(dx: number, dy: number): void {
    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
    this.move.x = stickCurve(dx / STICK_RADIUS);
    this.move.y = -stickCurve(dy / STICK_RADIUS);
  }

  // ---- the buttons

  private buildButtons(): void {
    this.cluster.replaceChildren();
    this.buttons.clear();
    for (const def of BUTTONS[this.mode]) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `touch-btn${def.size ? ` ${def.size}` : ""}`;
      b.textContent = def.label;
      if (def.id === "tool") {
        this.showTool(b);
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          this.tool = this.tool === "dig" ? "place" : "dig";
          this.showTool(b);
          this.showButtons();
        });
      } else if (def.hold) {
        const hold = def.hold;
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          b.setPointerCapture(e.pointerId);
          b.classList.add("down");
          this.held.add(hold);
        });
        const release = () => {
          b.classList.remove("down");
          this.held.delete(hold);
        };
        b.addEventListener("pointerup", release);
        b.addEventListener("pointercancel", release);
        b.addEventListener("lostpointercapture", release);
      } else if (def.press || def.leave) {
        const action = def.press;
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          b.classList.add("down");
          if (action) this.onPress(action);
          else this.onLeave();
        });
        const release = () => b.classList.remove("down");
        b.addEventListener("pointerup", release);
        b.addEventListener("pointercancel", release);
      }
      this.buttons.set(def.id, b);
      this.cluster.append(b);
    }
    this.showButtons();
  }

  /** Only the buttons with something to do now. Shown and hidden, not rebuilt: a thumb may be holding one of the others. */
  private showButtons(): void {
    const shown = { placing: this.tool === "place", adrift: this.situation.adrift, canThrow: this.situation.canThrow, canTurn: this.situation.canTurn };
    for (const def of BUTTONS[this.mode]) {
      const b = this.buttons.get(def.id);
      if (!b || !def.when) continue;
      b.hidden = !shown[def.when];
      if (b.hidden) {
        b.classList.remove("down");
        if (def.hold) this.held.delete(def.hold);
      }
    }
    const out = this.buttons.get("out");
    if (out) out.textContent = this.situation.lineOut ? "Let go" : "Climb out";
  }

  private showTool(b: HTMLButtonElement): void {
    b.textContent = this.tool === "dig" ? "⛏ Dig" : "▦ Place";
    b.classList.toggle("on", this.tool === "place");
  }

  private smallButton(label: string, title: string, go: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "touch-btn small";
    b.textContent = label;
    b.title = title;
    b.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      go();
    });
    return b;
  }

  /** Nothing held: the controls are hidden, or the page lost the fingers (another app, a notification). */
  releaseAll(): void {
    this.releaseButtons();
    this.stickPointer = null;
    this.stick.classList.remove("on");
    this.tilt(0, 0);
  }

  /** No button held and no finger on the world: the buttons are being rebuilt. */
  private releaseButtons(): void {
    this.held.clear();
    this.fingers.clear();
    this.worldDown = false;
    for (const b of this.buttons.values()) b.classList.remove("down");
  }
}
